import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe2 } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_4px_24px_-12px_rgba(14,42,63,0.18)]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 group">
            <span className="relative flex h-11 w-11 items-center justify-center rounded-lg bg-[#0E2A3F] text-white shadow-md transition-transform group-hover:scale-105">
              <Globe2 className="h-5 w-5" strokeWidth={1.8} />
              <span className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-[#F26F21] ring-2 ring-white" />
            </span>
            <span className="leading-tight">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-[#F26F21] font-semibold">
                Global Trade
              </span>
              <span className="block font-[Outfit] font-bold text-[#0E2A3F] text-lg">
                VRVR <span className="font-normal text-[#475569]">Import &amp; Exports</span>
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-[#F26F21]" : "text-[#0E2A3F] hover:text-[#F26F21]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute left-4 right-4 -bottom-0.5 h-[2px] rounded-full bg-[#F26F21] transition-transform origin-left ${
                        isActive ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="navbar-quote-cta"
              className="inline-flex items-center gap-2 rounded-md bg-[#F26F21] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#D95B14] transition-colors"
            >
              Get a Quote
              <span aria-hidden>→</span>
            </Link>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-md border border-slate-200 text-[#0E2A3F] hover:bg-slate-50"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div data-testid="navbar-mobile-menu" className="lg:hidden pb-5 pt-2 border-t border-slate-200">
            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-mobile-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-sm font-medium ${
                      isActive
                        ? "bg-[#0E2A3F] text-white"
                        : "text-[#0E2A3F] hover:bg-slate-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="navbar-mobile-quote-cta"
                className="mt-2 inline-flex items-center justify-center rounded-md bg-[#F26F21] px-4 py-3 text-sm font-semibold text-white"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
